/**
 * The district's labour, and the payroll.
 *
 * §4 makes a facility's output `min(power, water, staffing) × capacity`. Two of
 * those three terms arrived with the flows (ADR-013, ADR-014) and the third has
 * been missing ever since — `FacilityManager`'s header says so in as many words,
 * and ADR-015 closed by saying staffing was next. This is the third term.
 *
 * `DESIGN-GOVERNORS.md` §6 is unusually specific about what it has to be:
 *
 * > **Staffing is real labor.** Staff are families residing in that district who
 * > hold jobs at the facility; they commute by car (the existing mission system)
 * > every season-day. Unstaffed capacity produces nothing. Nobody can conscript a
 * > family — labor supply is downstream of wages, housing, and quality of life
 * > (§10). This is the mayors' quiet, permanent leverage.
 *
 * Four claims, and this file is built so that each of them is either true or
 * visibly absent:
 *
 *   1. **Staff are families.** A job is held by a named family living in a house
 *      on this map, and it goes away when they do. There is no workforce pool, no
 *      headcount, no abstraction over people — the roster is a list of family ids.
 *   2. **They commute.** A family with a job drives to it, through the mission
 *      system that has always driven them to the shops, and the traffic that
 *      results is ordinary traffic in every respect. See `HouseManager` and
 *      `CarManager`; nothing in *this* file knows what a car is.
 *   3. **Unstaffed capacity produces nothing.** The staffing fraction is a term in
 *      the `min`, so a facility nobody works at has an uptime of zero however much
 *      power it is drinking.
 *   4. **Nobody can conscript a family.** The one claim that is only half true
 *      here, and the half that is missing is named: hiring is *offered* and always
 *      *accepted*, because the things a family would weigh — wages against
 *      commute, housing, quality of life — are §10, and §10 does not exist. What
 *      does exist is the other direction: the mayor can call them out, and until
 *      they are called back nobody works. See `withdraw`.
 *
 * ## What a job is, here
 *
 * A pair — `(familyId, facilityId)` — and a wage. Not a role, not a skill, not a
 * productivity. The facility's whole demand for labour is one integer from §6's
 * table (a data centre wants eight families), and its whole satisfaction of that
 * demand is how many of the eight are filled and not on strike.
 *
 * ## Every number here is counted
 *
 * Law 1: the staffing fraction is filled posts over posted ones, the payroll is a
 * head count times a rate, and the ledger is a sum over cycles that were actually
 * worked. Nothing is rolled — this class holds no `Rng`, because who gets a job is
 * decided by where they live and ties are broken by who moved in first.
 *
 * ## What this class cannot see
 *
 * A hash type. Not the houses, not the facilities, not the ground, and not — the
 * point of the arrangement — anything under the soil. It is handed a bag of
 * postings and a bag of candidates once per budget cycle and hands back a roster
 * and a payroll; `SurfaceSim` is what knows that a posting is a data centre and a
 * candidate is the Harrisons. That is the standard `SeasonManager` and the two
 * flows are held to, for the reason `tests/purity.test.ts` gives: everything this
 * file counts ends up in a season record, and a season record is the public
 * document Phase 3 publishes.
 *
 * See `docs/staffing.md`.
 */

import { Canonical } from '../hashState';

/**
 * What a job pays. A world parameter, not state.
 *
 * §6's revenue split gives a host district four streams and this build has one of
 * them: *"Wages | Staff families' cash | Wage rate negotiated; families spend it
 * into local corps (existing subscription economy)"*. The other three — land
 * lease, local procurement, tolls — are all paid to or negotiated by somebody who
 * does not exist yet, and inventing them here would be inventing a polity.
 */
export interface LabourConfig {
  /**
   * What one facility job pays its family, per budget cycle.
   *
   * §7's worked exchange has a governor advertising *"8 positions at 1.5× median
   * wage"*, which is the only figure the design gives and is a *ratio*. Parkington
   * has no wages to be the median of — a family's income is the UBI and its
   * dividends — so the UBI is what 1.5× is taken against: 30 a cycle becomes 45.
   * The number is therefore chosen, and what it was chosen against is written
   * down, which is the standard ADR-015 D87 set for a facility's draw.
   */
  WAGE_PER_CYCLE: number;
}

export const DEFAULT_LABOUR_CONFIG: LabourConfig = {
  WAGE_PER_CYCLE: 45,
};

/** A facility's demand for labour, and where its staff have to get to. */
export interface JobPosting {
  facilityId: number;
  /** Families it needs. §6's table; 8 for a data centre. */
  staff: number;
  /** The gate cell. Only ever used to decide who lives nearest. */
  x: number;
  y: number;
}

/** A family that could take a job, where it lives, and whether it is at work. */
export interface LabourCandidate {
  familyId: number;
  x: number;
  y: number;
  /**
   * The facility this family is standing in right now, or `null`.
   *
   * The payroll's only input other than the roster. A wage is paid for a shift
   * that happened, so somebody at home, in a car, or at the shops is not paid
   * for this cycle — and a family who cannot *reach* their workplace is never
   * paid at all. See `payroll`.
   */
  atFacility: number | null;
}

/**
 * The district as the labour market is allowed to see it.
 *
 * A bag of ids and coordinates, assembled by `SurfaceSim` — the same discipline
 * the flows and the settler are held to. The grid dimensions are in it because
 * the map wraps and hiring order is a distance question; without them this class
 * would think two neighbours across the east edge lived a district apart.
 */
export interface LabourReading {
  cols: number;
  rows: number;
  /** Postings in facility id order, which is siting order. */
  postings: JobPosting[];
  /** Candidates in family id order, which is the order they moved in. */
  candidates: LabourCandidate[];
}

/** The labour market's complete state. See `LabourManager.save()`. */
export interface LabourSnapshot {
  /** `[familyId, facilityId]`, ascending by family id. */
  roster: Array<[number, number]>;
  /** Facilities under a withdrawal, ascending. */
  withdrawn: number[];
  /** Families standing in their own workplace at the last settled cycle. */
  onShift: number[];
  posts: number;
  staffed: number;
  meteredCycles: number;
  staffedCycles: number;
  vacancyCycles: number;
  withdrawnCycles: number;
  shiftCycles: number;
  wagesPaid: number;
  wageCycles: number;
}

/** One facility's labour, as a panel or a renderer wants it. */
export interface FacilityStaffing {
  /** Posts §6's table asks for. */
  posts: number;
  /** Posts a family holds. */
  hired: number;
  /**
   * Posts that count toward the staffing fraction: held, and not withdrawn.
   *
   * Deliberately not "how many are at their desks right now". Attendance decides
   * the *wage* and not the output (ADR-017 D102), and a panel figure that
   * flickered as eight families drove back and forth would be reporting the
   * traffic rather than the staffing.
   */
  working: number;
  /** Whether the mayor has called this facility's staff out. */
  withdrawn: boolean;
}

/**
 * The district's labour market.
 *
 * Owned by `SurfaceSim`, settled once per metered budget cycle beside the two
 * flows, snapshotted inside it. Holds no `Rng`: who is hired is decided by
 * distance and ties by id, so there is nothing here to roll.
 */
export class LabourManager {
  private config: LabourConfig;

  /** Who works where. `familyId → facilityId`. */
  private readonly roster = new Map<number, number>();

  /** Facilities whose staff the mayor has called out. */
  private readonly withdrawn = new Set<number>();

  /**
   * Who was standing in their own workplace at the last settled cycle.
   *
   * Not derived from the roster: it is a fact about where people physically are,
   * handed in with the reading, and it is what the payroll pays against.
   */
  private readonly onShift = new Set<number>();

  // --- The last settled cycle ------------------------------------------------

  /** Posts the estate advertised. */
  private posts = 0;
  /** Posts that were filled and worked. */
  private staffed = 0;

  // --- The ledger ------------------------------------------------------------

  private meteredCycles = 0;
  private staffedCycles = 0;
  private vacancyCycles = 0;
  private withdrawnCycles = 0;
  /**
   * Family-cycles somebody was actually standing in their workplace.
   *
   * Distinct from `staffedCycles`, which counts posts *held*. The gap between the
   * two is commuting, and in a district whose roads do not reach the gate it is
   * the whole of `staffedCycles`.
   */
  private shiftCycles = 0;
  private wagesPaid = 0;
  /** Family-cycles paid for. Equal to `shiftCycles` while the wage is positive. */
  private wageCycles = 0;

  constructor(config: Partial<LabourConfig> = {}) {
    this.config = { ...DEFAULT_LABOUR_CONFIG, ...config };
  }

  getConfig(): LabourConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<LabourConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  // --- The roster ------------------------------------------------------------

  /** Whether anybody in this district holds a federal job. The cheap question. */
  get employed(): boolean {
    return this.roster.size > 0;
  }

  /** Families holding a job. */
  get hired(): number {
    return this.roster.size;
  }

  /** Where this family works, or `null`. Asked once per family per update tick. */
  jobOf(familyId: number): number | null {
    return this.roster.get(familyId) ?? null;
  }

  /** Whether this facility's staff have been called out. */
  isWithdrawn(facilityId: number): boolean {
    return this.withdrawn.has(facilityId);
  }

  /** Facilities under a withdrawal, ascending. */
  strikes(): number[] {
    return Array.from(this.withdrawn).sort((a, b) => a - b);
  }

  /** Families working at this facility, ascending. Empty during a withdrawal. */
  staffOf(facilityId: number): number[] {
    if (this.withdrawn.has(facilityId)) return [];
    return this.rosterFamilies().filter((id) => this.roster.get(id) === facilityId);
  }

  /** Families on this facility's roster, ascending, strike or no strike. */
  rosterOf(facilityId: number): number[] {
    return this.rosterFamilies().filter((id) => this.roster.get(id) === facilityId);
  }

  /** Families who were standing in this facility at the last settled cycle. */
  onShiftAt(facilityId: number): number[] {
    return this.shiftFamilies().filter((id) => this.roster.get(id) === facilityId);
  }

  // --- The mayor's verb ------------------------------------------------------

  /**
   * Call one facility's staff out. Returns whether anything changed.
   *
   * Nobody is fired: the roster is untouched, and a family under a withdrawal is
   * still this facility's employee — it simply does not go, is not counted in the
   * staffing fraction, and is not paid. That is what makes the verb reversible in
   * one word, which is what §18 gives it (`restore_staffing`, no argument).
   *
   * A withdrawal against a facility that is not there is accepted and does
   * nothing, in the sense `CommandOutcome.applied` means by nothing: a well-formed
   * command, correctly ordered, with no effect. It is *not* remembered against a
   * facility that might be sited later — a strike against a building nobody has
   * built is not a labour dispute.
   */
  withdraw(facilityId: number, sited: boolean): boolean {
    if (!sited) return false;
    if (this.withdrawn.has(facilityId)) return false;
    this.withdrawn.add(facilityId);
    return true;
  }

  /** End every withdrawal. Returns whether there was one. */
  restore(): boolean {
    if (this.withdrawn.size === 0) return false;
    this.withdrawn.clear();
    return true;
  }

  // --- The market ------------------------------------------------------------

  /**
   * Settle one budget cycle: release the gone, hire the near, record the rest.
   *
   * Called once per settled budget cycle, from the same block as the two flows.
   * Three things happen, in this order and for these reasons:
   *
   *   1. **Release.** A job whose family has left the district, or whose facility
   *      is not on the map, is not a job. Families leave often — three cycles in
   *      the red and `EconomyManager` evicts them — so this is the common case and
   *      not the exceptional one.
   *   2. **Hire.** Every vacancy is offered to the nearest unemployed family, and
   *      accepted. Postings are walked in facility id order, which is siting
   *      order; candidates are walked by distance to the gate with ties broken by
   *      family id, which is the order they moved in. Both are promises rather
   *      than incidental orders (`docs/determinism.md` rule 4) — with two
   *      facilities on one map, which of them gets the family living between them
   *      is a fact about the world and not about a `Map`'s iteration.
   *   3. **Record.** Posts, filled posts, and the cycle counters the season
   *      record subtracts over.
   *
   * A withdrawal changes none of this. Struck staff keep their jobs and keep their
   * vacancies filled — a strike that let the polity hire replacements the next
   * cycle would not be a strike, it would be a resignation.
   */
  settle(reading: LabourReading): void {
    const posted = new Set<number>();
    for (const posting of reading.postings) posted.add(posting.facilityId);

    const available = new Set<number>();
    for (const candidate of reading.candidates) available.add(candidate.familyId);

    // 1. Release. Ascending, so the walk does not depend on insertion order even
    //    though the result could not: every decision here is independent.
    for (const familyId of this.rosterFamilies()) {
      const facilityId = this.roster.get(familyId)!;
      if (!available.has(familyId) || !posted.has(facilityId)) {
        this.roster.delete(familyId);
      }
    }
    // A strike against a facility that is no longer posted is over, for the same
    // reason. Nothing can un-site a facility today; this is what keeps that from
    // being an assumption the class depends on.
    for (const facilityId of this.strikes()) {
      if (!posted.has(facilityId)) this.withdrawn.delete(facilityId);
    }

    // 2. Hire.
    for (const posting of reading.postings) {
      const vacancies = posting.staff - this.rosterOf(posting.facilityId).length;
      if (vacancies <= 0) continue;

      const nearest = this.byDistance(reading, posting).filter(
        (candidate) => !this.roster.has(candidate.familyId)
      );
      for (let i = 0; i < vacancies && i < nearest.length; i++) {
        this.roster.set(nearest[i].familyId, posting.facilityId);
      }
    }

    // 3. Record who actually turned up, before anything is counted. A family is
    //    on shift when it is standing in the facility that employs it — being at
    //    somebody else's would not be work, and neither is being on the road to
    //    your own.
    this.onShift.clear();
    for (const candidate of reading.candidates) {
      if (candidate.atFacility === null) continue;
      if (this.roster.get(candidate.familyId) !== candidate.atFacility) continue;
      if (this.withdrawn.has(candidate.atFacility)) continue;
      this.onShift.add(candidate.familyId);
    }

    // 4. Record the rest.
    let posts = 0;
    let staffed = 0;
    for (const posting of reading.postings) {
      posts += posting.staff;
      staffed += this.staffOf(posting.facilityId).length;
    }
    this.posts = posts;
    this.staffed = staffed;

    if (posts > 0) {
      this.meteredCycles++;
      this.staffedCycles += staffed;
      this.vacancyCycles += Math.max(0, posts - staffed);
      this.withdrawnCycles += this.withdrawn.size;
      this.shiftCycles += this.onShift.size;
    }
  }

  /**
   * Candidates sorted by how far they live from a posting's gate.
   *
   * Manhattan distance on a map that wraps, which is the same measure
   * `CarManager` navigates by, so the family the market calls nearest is the one
   * with the shortest drive rather than the one that happens to be on the same
   * side of the origin. Ties break by family id — the order they moved in — which
   * is what keeps a hiring round from depending on how the candidate list was
   * built.
   */
  private byDistance(reading: LabourReading, posting: JobPosting): LabourCandidate[] {
    const distance = (candidate: LabourCandidate): number => {
      const dx = Math.abs(candidate.x - posting.x);
      const dy = Math.abs(candidate.y - posting.y);
      return Math.min(dx, reading.cols - dx) + Math.min(dy, reading.rows - dy);
    };
    return reading.candidates
      .slice()
      .sort((a, b) => distance(a) - distance(b) || a.familyId - b.familyId);
  }

  // --- The payroll -----------------------------------------------------------

  /**
   * What the polity owes this district's families this cycle, ascending by id.
   *
   * Read and paid by `SurfaceSim`, because family cash lives in `HouseManager` and
   * this class cannot see one. Ascending because each payment is a float addition
   * onto a balance somebody else spends, and the order of float additions is a
   * decision (`docs/determinism.md` rule 4).
   *
   * **Paid for the shift, not for the post.** A family is on this list only if it
   * was standing in the facility that employs it when the cycle was settled.
   * Somebody at home between shifts is not paid for that cycle; somebody in a car
   * is not paid for the drive; and a district whose roads do not reach the gate
   * has employees who are never paid a cent, because they never arrive.
   *
   * That is a different rule from the one the *staffing fraction* follows, and
   * the split is deliberate. Holding the post is what the polity contracted for
   * and what §4's `min` measures; turning up is what a wage is for. A facility
   * whose staff are all stuck in traffic is fully staffed and paying nobody.
   *
   * Struck staff are absent from the list, because a withdrawal keeps them home.
   * §10: *"Output stops; wages stop."*
   *
   * Nothing is deducted from anything. A wage is federal money entering a district
   * from outside it, and the account it leaves is the polity's — which does not
   * exist, so `wagesPaid` accumulates against nobody. That is the same shape the
   * two flows shipped in: the measurement first, the party who is billed for it
   * when there is one (§8's tithe and leases, Phase 3).
   */
  payroll(): Array<[number, number]> {
    const wage = this.config.WAGE_PER_CYCLE;
    if (wage <= 0) return [];
    const out: Array<[number, number]> = [];
    for (const familyId of this.rosterFamilies()) {
      if (!this.onShift.has(familyId)) continue;
      out.push([familyId, wage]);
    }
    return out;
  }

  /** Book a payroll that has actually been paid into somebody's cash. */
  recordPayroll(payments: ReadonlyArray<[number, number]>): void {
    for (const [, amount] of payments) {
      this.wagesPaid += amount;
      this.wageCycles++;
    }
  }

  // --- The third term --------------------------------------------------------

  /**
   * What fraction of the estate's posts were worked at the last settled cycle.
   *
   * 1 when nothing is posted, which is why a district that has never hosted a
   * facility reads the same as one whose data centre is fully crewed: there is
   * nothing to distinguish, and the `min` in `FacilityManager.meter` has to be
   * harmless in a world with no labour market in it.
   *
   * Estate-wide rather than per facility, which is the same shape
   * `PowerManager.getFacilityFraction()` and `WaterManager.getFacilityFraction()`
   * already have. Rationing *between* facilities is one question, not three, and
   * it arrives the first time a district hosts two of them.
   */
  getStaffedFraction(): number {
    if (this.posts <= 0) return 1;
    return Math.min(1, Math.max(0, this.staffed / this.posts));
  }

  /** Posts and heads at the last settled cycle. */
  getStaffing(): { posts: number; staffed: number } {
    return { posts: this.posts, staffed: this.staffed };
  }

  /** One facility's labour, for a panel. `posts` is the caller's; it knows §6. */
  getFacilityStaffing(facilityId: number, posts: number): FacilityStaffing {
    const hired = this.rosterOf(facilityId).length;
    return {
      posts,
      hired,
      working: this.withdrawn.has(facilityId) ? 0 : hired,
      withdrawn: this.withdrawn.has(facilityId),
    };
  }

  /** The lifetime figures. Monotone sums, so a season's labour is a subtraction. */
  getLedger() {
    return {
      meteredCycles: this.meteredCycles,
      staffedCycles: this.staffedCycles,
      vacancyCycles: this.vacancyCycles,
      withdrawnCycles: this.withdrawnCycles,
      shiftCycles: this.shiftCycles,
      wagesPaid: this.wagesPaid,
      wageCycles: this.wageCycles,
    };
  }

  // --- State ----------------------------------------------------------------

  /** Family ids on the roster, ascending. See `docs/determinism.md` rule 4. */
  private rosterFamilies(): number[] {
    return Array.from(this.roster.keys()).sort((a, b) => a - b);
  }

  /** Family ids on shift, ascending. Same rule, same reason. */
  private shiftFamilies(): number[] {
    return Array.from(this.onShift).sort((a, b) => a - b);
  }

  save(): LabourSnapshot {
    return {
      roster: this.rosterFamilies().map((id) => [id, this.roster.get(id)!] as [number, number]),
      withdrawn: this.strikes(),
      onShift: this.shiftFamilies(),
      posts: this.posts,
      staffed: this.staffed,
      meteredCycles: this.meteredCycles,
      staffedCycles: this.staffedCycles,
      vacancyCycles: this.vacancyCycles,
      withdrawnCycles: this.withdrawnCycles,
      shiftCycles: this.shiftCycles,
      wagesPaid: this.wagesPaid,
      wageCycles: this.wageCycles,
    };
  }

  /**
   * Restore from `save()`.
   *
   * Including the strike. A district restored with everybody back at work is a
   * district whose labour dispute ended because somebody reloaded, which is the
   * kind of silent difference a total snapshot exists to refuse — the same
   * sentence `SurfaceSim.restore` already says about the weather.
   */
  load(state: LabourSnapshot): void {
    this.roster.clear();
    for (const [familyId, facilityId] of state.roster) this.roster.set(familyId, facilityId);
    this.withdrawn.clear();
    for (const facilityId of state.withdrawn) this.withdrawn.add(facilityId);
    this.onShift.clear();
    for (const familyId of state.onShift) this.onShift.add(familyId);
    this.posts = state.posts;
    this.staffed = state.staffed;
    this.meteredCycles = state.meteredCycles;
    this.staffedCycles = state.staffedCycles;
    this.vacancyCycles = state.vacancyCycles;
    this.withdrawnCycles = state.withdrawnCycles;
    this.shiftCycles = state.shiftCycles;
    this.wagesPaid = state.wagesPaid;
    this.wageCycles = state.wageCycles;
  }

  /**
   * Order-stable view of the market, for hashing — and nothing while there is no
   * labour market.
   *
   * The move the two flows, the estate and the Chronicle all make: a district
   * where nobody has ever held a federal job and no facility has ever posted one
   * is a district in which this class did not happen, so the key is omitted rather
   * than written as a bag of zeroes. That is what lets a labour market arrive
   * without moving a single hash recorded before there was one — every corpus
   * entry in this repository is a district nobody sited anything on, so nobody in
   * it was ever hired.
   *
   * The roster is in it as well as the totals: two districts that agree on how
   * many people are working and disagree about *which* families they are will pay
   * different people next cycle, and the two economies diverge from there.
   */
  canonicalState(): Canonical | undefined {
    if (this.roster.size === 0 && this.meteredCycles === 0 && this.withdrawn.size === 0) {
      return undefined;
    }
    return {
      roster: this.rosterFamilies().map((id) => `${id}:${this.roster.get(id)}`),
      withdrawn: this.strikes(),
      onShift: this.shiftFamilies(),
      posts: this.posts,
      staffed: this.staffed,
      meteredCycles: this.meteredCycles,
      staffedCycles: this.staffedCycles,
      vacancyCycles: this.vacancyCycles,
      withdrawnCycles: this.withdrawnCycles,
      shiftCycles: this.shiftCycles,
      wagesPaid: this.wagesPaid,
      wageCycles: this.wageCycles,
    };
  }
}
